import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { find, filter } from 'lodash'
import dayjs from 'dayjs'

import Operations from '../../../../operations'
import Selectors from '../../../../selectors'
import { ChartLogList, SectionHeader } from '../../../../components/generic'
import StockListItem from '../ResidentEmarScreens/EMARResidentStockScreen/StockListItem'
import { EMARMedicationScreenDetailsSection } from '../ResidentEmarScreens/components'
import { withRouter } from '../../../../withRouter'
import useQuery from '../../../../hooks/useQuery'
import { Card, EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { EmptyStateDescription } from '@lmc/ui-kit/src/dataDisplay/EmptyState'
import useFeatureFlags from 'hooks/useFeatureFlags'

const renderStock = (props) => {
  const { medication, resident, stockItem, testId } = props
  return (
    <div
      style={styles.stockContainer}
      data-testid={
        testId
          ? `${testId}-med-chart-stock-container`
          : 'med-chart-stock-container'
      }
    >
      <StockListItem
        label="Stock"
        icon={stockItem && stockItem.icon}
        medication={medication}
        log={medication.stockLog}
        residentId={resident.id}
        testId={testId}
      />
    </div>
  )
}

// Medication Details section
const renderMedicationDetailsSection = (props) => {
  const {
    medication,
    emarStockEnabled,
    emarRoundTimings,
    isPDF,
    resident,
    stateProps,
    user,
    testId,
  } = props

  return (
    <EMARMedicationScreenDetailsSection
      emarStockEnabled={emarStockEnabled}
      medication={medication}
      emarRoundTimings={emarRoundTimings}
      isPDF={isPDF}
      resident={resident}
      user={user}
      testId={testId}
      {...stateProps}
    />
  )
}

const renderLogsEmptyState = (testId) => {
  const helpLink =
    'https://support.logmycare.co.uk/en/articles/4851799-how-do-i-sign-a-prn-medication-in-the-carer-app'

  return (
    <Card
      data-testid={
        testId
          ? `${testId}-med-chart-logs-empty-card`
          : 'med-chart-logs-empty-card'
      }
    >
      <EmptyState
        icon={<NoDataIcon />}
        title="No updates during this period"
        description={
          <EmptyStateDescription>
            Once you've signed off a MAR chart or added a comment in the Carer
            <br />
            App, they'll appear here. Learn more about signing medications{' '}
            <a href={helpLink} target="_blank" rel="noreferrer">
              here
            </a>
            .{' '}
          </EmptyStateDescription>
        }
        data-testid={
          testId
            ? `${testId}-med-chart-logs-empty-state`
            : 'med-chart-logs-empty-state'
        }
      ></EmptyState>
    </Card>
  )
}

const renderLogsList = (props) => {
  const { logs, testId } = props

  return (
    <div
      style={styles.logListContainer}
      data-testid={
        testId
          ? `${testId}-med-chart-logs-list-container`
          : 'med-chart-logs-list-container'
      }
    >
      <ChartLogList logs={logs} style={styles.logList} testId={testId} />
    </div>
  )
}

const renderMedicationLogs = (props) => {
  const { logs, isPDF, testId } = props

  return (
    <div
      style={isPDF ? styles.pageBreak : styles.logsSectionContainer}
      data-testid={
        testId ? `${testId}-med-chart-logs-section` : 'med-chart-logs-section'
      }
    >
      <SectionHeader
        greyLine
        isSubSection
        title={logs.length ? 'Logs' : 'Updates'}
        titleStyles={{ fontWeight: 400 }}
        data-testid={
          testId ? `${testId}-med-chart-logs-header` : 'med-chart-logs-header'
        }
      />
      {!logs.length ? renderLogsEmptyState(testId) : renderLogsList(props)}
    </div>
  )
}

/**
@description Renders screen for an individual medication
@param {array} emarRoundTimings. The different rounds for that home e.g. 14:00, 19:00 etc.
Not all medications are administered at every round.
@param {array} logs. The logs for the medication
@param {object} medication. The medication.
@param {function} openDiscontinueMedModal. Opens the DiscontinueMed modal.
@param {object} resident. The resident.
@returns {function} ChartScreen
*/

export function ResidentMedChartScreen(props) {
  const {
    emarStockEnabled,
    fetchData,
    fetchLogs,
    logs,
    medication: { deleted, deletedOn },
    router: { params },
    testId,
  } = props
  const flags = useFeatureFlags({ flagNames: ['emar_v2_chart'] })?.data
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false

  // PDF export works by rendering the screen with search params
  // Eg. /residents/602fc3830ba1fe8d6e4a7473/pdf/emar?printpdf=1&startDate=...
  const query = useQuery()
  const isPDF = query.get('printpdf')
  const startDateQuery = query.get('startDate')
  const endDateQuery = query.get('endDate')

  let defaultEndDate
  let defaultStartDate

  if (startDateQuery) defaultStartDate = dayjs(startDateQuery)
  else if (deleted && deletedOn)
    defaultStartDate = dayjs(deletedOn).subtract(14, 'days')
  else defaultStartDate = dayjs().subtract(14, 'days')

  if (endDateQuery) defaultEndDate = dayjs(endDateQuery)
  else if (deleted && deletedOn) defaultEndDate = dayjs(deletedOn)
  else defaultEndDate = dayjs()

  const [startDate, setStartDate] = React.useState(defaultStartDate)
  const [endDate, setEndDate] = React.useState(defaultEndDate)
  const [viewMedDetails, setViewMedDetails] = React.useState(true)

  const medicationId = params.medication_id

  const residentId = params.id

  useEffect(() => {
    if (medicationId && residentId) {
      fetchData({
        medicationId,
        residentId,
        startDate,
        endDate,
        emarV2ChartEnabled,
      })
    }
    if (medicationId) {
      fetchLogs(medicationId)
    }
  }, [startDate, endDate, medicationId, residentId, emarV2ChartEnabled])

  const stateProps = {
    endDate,
    startDate,
    setEndDate,
    setStartDate,
    setViewMedDetails,
    viewMedDetails,
  }

  if (!logs) return null

  return (
    <div
      data-testid={
        testId
          ? `${testId}-resident-med-chart-screen`
          : 'resident-med-chart-screen'
      }
    >
      {emarStockEnabled && renderStock({ ...props, testId })}
      {renderMedicationDetailsSection({ ...props, stateProps, isPDF, testId })}
      {renderMedicationLogs({ ...props, isPDF, testId })}
    </div>
  )
}

ResidentMedChartScreen.propTypes = {
  emarRoundTimings: PropTypes.array.isRequired,
  emarStockEnabled: PropTypes.array.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  logs: PropTypes.array.isRequired,
  medication: PropTypes.object.isRequired,
  resident: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  testId: PropTypes.string,
}

const styles = {
  logList: {
    marginLeft: 5,
    marginTop: 40,
  },
  logsSectionContainer: {
    paddingTop: 24,
  },
  logListContainer: {
    display: 'flex',
  },
  medRoundIcon: {
    margin: '-2px 4px 0px -4px',
    maxHeight: 20,
  },
  pageBreak: {
    pageBreakAfter: 'always',
    pageBreakInside: 'avoid',
    paddingTop: 38,
  },
  stockContainer: {
    marginTop: 16,
  },
}

const mapStateToProps = (state, props) => ({
  emarStockEnabled: state.global.home.features?.eMARStock?.enabled,
  emarRoundTimings: state.data['emar-round-timings'],
  logs: filter(Selectors.populateLogs(state), {
    emarResidentMedication: props.router.params.medication_id,
  }),
  medication: Selectors.getMedicationMarChart(state, props),
  stockItem: find(state.data['log-category-items'], {
    itemId: 'emarStockCount',
  }),
  resident: Selectors.getResidentProfile(state, { id: props.router.params.id }),
  user: Selectors.getCurrentUserProfile(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: ({ medicationId, residentId, startDate, endDate, emarV2ChartEnabled }) => {
    if (emarV2ChartEnabled) {
      dispatch(
        Operations.loadList(
          'emar-round-timings',
          undefined,
          true,
          '/emar/legacy/emar-round-timings',
        ),
      )
    } else {
      dispatch(Operations.loadList('emar-round-timings'))
    }
    dispatch(
      Operations.loadList(
        'emar-rounds',
        {
          resident: residentId,
          from: dayjs(startDate).startOf('day').toISOString(),
          to: dayjs(endDate).endOf('day').toISOString(),
        },
        true,
        emarV2ChartEnabled
          ? '/emar/medication-administration-instances/legacy/emar-rounds'
          : undefined,
      ),
    )
    dispatch(
      Operations.loadListItem(
        'emar-resident-medications',
        medicationId,
        undefined,
        emarV2ChartEnabled
          ? `/emar/medication-schedule-patterns/legacy/emar-resident-medications/${medicationId}`
          : undefined,
      ),
    )
  },
  fetchLogs: (medicationId) =>
    dispatch(
      Operations.loadList('logs', { emarResidentMedication: medicationId }),
    ),
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ResidentMedChartScreen),
)
